/**
 * Drag-and-drop image upload with a per-file status list and a crop step.
 *
 * Three libraries were considered. FilePond's interactive editor is a paid product
 * and its free crop plugins only centre-crop to a ratio. Uppy's Dashboard does all
 * of this out of the box but its image editor pulls in Preact, which would ship a
 * second UI runtime beside React 19, and its Dashboard brings a visual language
 * this CRM does not use. So: `react-dropzone` for the drop target and
 * `react-easy-crop` for the crop window — two small, current packages — with the
 * status list owned here, where it can look like the rest of the admin.
 *
 * The component does not know where images go. It hands the caller a cropped data
 * URL and the preset it was cropped to, and the caller persists it. That keeps one
 * uploader usable for event art, portraits, and whatever comes next.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDropzone, type FileRejection, type FileError } from "react-dropzone";
import Cropper, { type Area } from "react-easy-crop";
import { AlertCircle, CheckCircle2, Crop, FileText, ImageUp, Loader2, Trash2, UploadCloud } from "lucide-react";
import { MAX_IMAGE_UPLOAD_BYTES, MAX_IMAGE_UPLOAD_LABEL } from "@shared/imageUploadLimits";
import {
  IMAGE_PRESETS,
  IMAGE_PRESETS_BY_KEY,
  presetAspect,
  type ImagePresetKey,
} from "@shared/imagePresets";
import { cropImageToDataUrl } from "@/lib/cropImage";

/**
 * What the picker offers, matching what the server will accept.
 *
 * SVG is deliberately absent. It is markup rather than pixels and can carry script,
 * so an SVG served back from our own origin under `image/svg+xml` is an XSS vector
 * against the admin session that uploaded it — which is exactly what the magic-byte
 * check in the photo and art pipelines exists to prevent. A vector source is not
 * worth reopening that, so vector art gets exported to PNG or PDF first.
 *
 * PDF is accepted as a *source*: the server rasterizes it and stores only the
 * raster, so no PDF is ever served back either.
 */
const ACCEPTED = {
  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/webp": [".webp"],
  "application/pdf": [".pdf"],
};

/**
 * Types the crop step cannot work on.
 *
 * The cropper draws to a canvas and re-encodes a raster image; a PDF has no single
 * frame to draw. A PDF therefore skips cropping and is uploaded as it arrived, and
 * the server rasterizes its first page to the chosen preset.
 */
const UNCROPPABLE_TYPES = new Set(["application/pdf"]);

type QueueStatus = "ready" | "cropping" | "uploading" | "done" | "failed";

type QueueItem = {
  id: string;
  file: File;
  /** An object URL for previewing and cropping. Revoked when the item is dropped. */
  objectUrl: string;
  status: QueueStatus;
  presetKey: ImagePresetKey;
  error: string | null;
};

const STATUS_META: Record<QueueStatus, { label: string; color: string }> = {
  ready: { label: "Ready to crop", color: "#94A3B8" },
  cropping: { label: "Cropping", color: "#67E8F9" },
  uploading: { label: "Uploading", color: "#FCD34D" },
  done: { label: "Uploaded", color: "#86EFAC" },
  failed: { label: "Failed", color: "#FDA4AF" },
};

export default function ImageUploader({
  onUpload,
  presetKeys,
  defaultPresetKey = "facebook_wall",
  maxFiles = 8,
  label = "Event art",
  description = "Drag images here, or choose files. Crop each one to the size it is for before it uploads.",
}: {
  /**
   * Persists one cropped image. Rejecting marks that row failed and leaves it in
   * the list to retry, so a transient error does not lose the crop.
   */
  onUpload: (dataUrl: string, presetKey: ImagePresetKey, file: File) => Promise<void>;
  /** Narrow the offered sizes. Omit for all of them. */
  presetKeys?: ImagePresetKey[];
  defaultPresetKey?: ImagePresetKey;
  maxFiles?: number;
  label?: string;
  description?: string;
}) {
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedArea, setCroppedArea] = useState<Area | null>(null);
  const [rejection, setRejection] = useState<string | null>(null);

  const presets = useMemo(
    () => (presetKeys ? IMAGE_PRESETS.filter(preset => presetKeys.includes(preset.key)) : IMAGE_PRESETS),
    [presetKeys],
  );

  /*
   * Object URLs are revoked on unmount rather than per-render. Holding them in a
   * ref keeps the cleanup effect from depending on `queue`, which would revoke a
   * live preview's URL on every keystroke that touched the list.
   */
  const objectUrls = useRef<string[]>([]);
  useEffect(() => () => {
    for (const url of objectUrls.current) URL.revokeObjectURL(url);
  }, []);

  const active = queue.find(item => item.id === activeId) ?? null;

  const onDrop = useCallback((accepted: File[], rejected: readonly FileRejection[]) => {
    setRejection(
      rejected.length === 0
        ? null
        : rejected.some(entry => entry.errors.some((error: FileError) => error.code === "file-too-large"))
          ? `Images must be ${MAX_IMAGE_UPLOAD_LABEL} or smaller.`
          : "Upload a PNG, JPEG, WebP, SVG or PDF file.",
    );

    const additions = accepted.map((file, index) => {
      const objectUrl = URL.createObjectURL(file);
      objectUrls.current.push(objectUrl);
      return {
        // Index included because two files dropped together can share a name and
        // a timestamp, and a duplicate key would collapse them into one row.
        id: `${file.name}-${file.size}-${index}-${objectUrls.current.length}`,
        file,
        objectUrl,
        status: "ready" as const,
        presetKey: defaultPresetKey,
        error: null,
      };
    });

    setQueue(current => [...current, ...additions].slice(0, maxFiles));
    /*
     * Open the first new file straight into the crop step: dropping a file and
     * then having to click it again is a step with no decision in it.
     *
     * Unless it is a PDF or an SVG, which the cropper cannot open — those wait in
     * the queue to be uploaded as they are.
     */
    const firstCroppable = additions.find(addition => !UNCROPPABLE_TYPES.has(addition.file.type));
    if (firstCroppable) openForCrop(firstCroppable.id);
  }, [defaultPresetKey, maxFiles]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED,
    maxSize: MAX_IMAGE_UPLOAD_BYTES,
    maxFiles,
  });

  function openForCrop(id: string) {
    setActiveId(id);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedArea(null);
    setQueue(current => current.map(item => (item.id === id ? { ...item, status: "cropping", error: null } : item)));
  }

  function updateItem(id: string, patch: Partial<QueueItem>) {
    setQueue(current => current.map(item => (item.id === id ? { ...item, ...patch } : item)));
  }

  function removeItem(id: string) {
    setQueue(current => current.filter(item => item.id !== id));
    if (activeId === id) setActiveId(null);
  }

  /**
   * Uploads a file the crop step cannot open — a PDF — exactly as it arrived.
   *
   * The preset still travels with it, because it is what the server rasterizes the
   * PDF's first page to. So the choice is not skipped, only made from the row instead
   * of from a crop window that could not show the document anyway.
   */
  async function uploadAsIs(item: QueueItem) {
    updateItem(item.id, { status: "uploading", error: null });

    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.addEventListener("load", () => resolve(String(reader.result)));
        reader.addEventListener("error", () => reject(new Error("That file could not be read.")));
        reader.readAsDataURL(item.file);
      });
      await onUpload(dataUrl, item.presetKey, item.file);
      updateItem(item.id, { status: "done" });
    } catch (error) {
      updateItem(item.id, {
        status: "failed",
        error: error instanceof Error ? error.message : "That file could not be uploaded.",
      });
    }
  }

  async function confirmCrop() {
    if (!active || !croppedArea) return;
    const preset = IMAGE_PRESETS_BY_KEY[active.presetKey];

    updateItem(active.id, { status: "uploading", error: null });
    setActiveId(null);

    try {
      const dataUrl = await cropImageToDataUrl({
        src: active.objectUrl,
        crop: croppedArea,
        targetWidth: preset.width,
        targetHeight: preset.height,
      });
      await onUpload(dataUrl, active.presetKey, active.file);
      updateItem(active.id, { status: "done" });
    } catch (error) {
      // Left in the list as `failed` rather than removed, so the file is still
      // there to crop again without re-picking it.
      updateItem(active.id, {
        status: "failed",
        error: error instanceof Error ? error.message : "That image could not be uploaded.",
      });
    }
  }

  const aspect = active ? presetAspect(active.presetKey) : undefined;

  return (
    <div>
      <p style={{ margin: "0 0 .3rem", color: "#CDD5E1", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750, letterSpacing: ".06em", textTransform: "uppercase" }}>{label}</p>
      <p style={{ margin: "0 0 .7rem", color: "#8390A6", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5 }}>{description}</p>

      {/* Drop target. Also a button, so the keyboard reaches the file picker. */}
      <div
        {...getRootProps()}
        style={{
          display: "grid",
          placeItems: "center",
          gap: ".4rem",
          padding: "1.6rem 1rem",
          borderRadius: ".9rem",
          border: `1.5px dashed ${isDragActive ? "rgba(233,30,140,.7)" : "rgba(255,255,255,.16)"}`,
          background: isDragActive ? "rgba(233,30,140,.08)" : "rgba(255,255,255,.025)",
          cursor: "pointer",
          transition: "border-color 160ms ease, background 160ms ease",
        }}
      >
        <input {...getInputProps()} />
        <UploadCloud size={26} aria-hidden="true" style={{ color: isDragActive ? "#F9A8D4" : "#64748B" }} />
        <p style={{ margin: 0, color: "#E2E8F0", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700 }}>
          {isDragActive ? "Drop to add" : "Drag images here, or click to choose"}
        </p>
        <p style={{ margin: 0, color: "#64748B", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>
          PNG, JPEG, WebP, or PDF · up to {MAX_IMAGE_UPLOAD_LABEL} each · {maxFiles} at a time
        </p>
      </div>

      {rejection && (
        <p role="alert" style={{ margin: ".6rem 0 0", display: "flex", alignItems: "center", gap: ".4rem", color: "#FDA4AF", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>
          <AlertCircle size={14} aria-hidden="true" /> {rejection}
        </p>
      )}

      {/* Crop step for the open file. */}
      {active && (
        <section aria-label={`Crop ${active.file.name}`} style={{ marginTop: "1rem", padding: ".9rem", borderRadius: ".9rem", border: "1px solid rgba(103,232,249,.22)", background: "var(--chi-table-surface)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: ".45rem", marginBottom: ".7rem", color: "#67E8F9", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750, letterSpacing: ".06em", textTransform: "uppercase" }}>
            <Crop size={14} aria-hidden="true" /> Crop · {active.file.name}
          </div>

          {/* Size choice first: it locks the crop window, so choosing it after cropping would discard the crop. */}
          <div role="group" aria-label="Output size" style={{ display: "flex", flexWrap: "wrap", gap: ".4rem", marginBottom: ".8rem" }}>
            {presets.map(preset => {
              const selected = preset.key === active.presetKey;
              return (
                <button
                  key={preset.key}
                  type="button"
                  onClick={() => { updateItem(active.id, { presetKey: preset.key }); setCrop({ x: 0, y: 0 }); setZoom(1); }}
                  aria-pressed={selected}
                  title={preset.hint}
                  style={{ minHeight: 38, textAlign: "left", padding: ".38rem .6rem", borderRadius: ".55rem", border: `1px solid ${selected ? "rgba(233,30,140,.6)" : "rgba(255,255,255,.12)"}`, background: selected ? "rgba(233,30,140,.14)" : "transparent", color: selected ? "#F9A8D4" : "#AEB9C9", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, cursor: "pointer" }}
                >
                  {preset.label}
                  <span style={{ display: "block", color: selected ? "rgba(249,168,212,.75)" : "#64748B", fontWeight: 400 }}>{preset.hint}</span>
                </button>
              );
            })}
          </div>

          <div style={{ position: "relative", height: 320, borderRadius: ".65rem", overflow: "hidden", background: "#05070D" }}>
            <Cropper
              image={active.objectUrl}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(_area: Area, pixels: Area) => setCroppedArea(pixels)}
              restrictPosition={false}
            />
          </div>

          <label style={{ display: "grid", gap: ".3rem", margin: ".8rem 0 0", color: "#AEB9C9", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700 }}>
            Zoom
            <input type="range" min={1} max={4} step={0.01} value={zoom} onChange={event => setZoom(Number(event.target.value))} style={{ accentColor: "#E91E8C" }} />
          </label>

          <div style={{ display: "flex", gap: ".5rem", marginTop: ".85rem", flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => void confirmCrop()}
              disabled={!croppedArea}
              style={{ minHeight: 42, display: "inline-flex", alignItems: "center", gap: ".4rem", padding: "0 1rem", borderRadius: ".6rem", border: "1px solid rgba(233,30,140,.5)", background: "linear-gradient(135deg,#E91E8C,#C2185B)", color: "white", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750, cursor: croppedArea ? "pointer" : "not-allowed", opacity: croppedArea ? 1 : .55 }}
            >
              <ImageUp size={15} aria-hidden="true" /> Crop and upload
            </button>
            <button
              type="button"
              onClick={() => { updateItem(active.id, { status: "ready" }); setActiveId(null); }}
              style={{ minHeight: 42, padding: "0 .9rem", borderRadius: ".6rem", border: "1px solid rgba(255,255,255,.14)", background: "transparent", color: "#AEB9C9", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, cursor: "pointer" }}
            >
              Cancel
            </button>
          </div>
        </section>
      )}

      {/* Status list. */}
      {queue.length > 0 && (
        <ul aria-label="Upload queue" style={{ listStyle: "none", margin: "1rem 0 0", padding: 0, display: "grid", gap: ".45rem" }}>
          {queue.map(item => {
            const status = STATUS_META[item.status];
            return (
              <li key={item.id} style={{ display: "flex", alignItems: "center", gap: ".7rem", padding: ".55rem .7rem", borderRadius: ".65rem", border: "1px solid rgba(255,255,255,.08)", background: "rgba(255,255,255,.025)" }}>
                {/*
                  An <img> pointed at a PDF object URL renders as a broken image, so a
                  document gets an icon instead of a thumbnail it cannot produce.
                */}
                {UNCROPPABLE_TYPES.has(item.file.type) ? (
                  <span aria-hidden="true" style={{ width: 38, height: 38, flex: "0 0 auto", display: "grid", placeItems: "center", borderRadius: ".4rem", background: "#05070D", border: "1px solid rgba(255,255,255,.1)", color: "#94A3B8" }}>
                    <FileText size={17} />
                  </span>
                ) : (
                  <img src={item.objectUrl} alt="" style={{ width: 38, height: 38, flex: "0 0 auto", borderRadius: ".4rem", objectFit: "cover", background: "#05070D" }} />
                )}
                <div style={{ minWidth: 0, flex: 1 }}>
                  <p style={{ margin: 0, color: "#E2E8F0", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.file.name}</p>
                  <p style={{ margin: 0, color: status.color, fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700 }}>
                    {/* "Ready to crop" would be a lie on a PDF, which is never cropped. */}
                    {item.status === "ready" && UNCROPPABLE_TYPES.has(item.file.type) ? "Ready to upload" : status.label}
                    {" · "}{IMAGE_PRESETS_BY_KEY[item.presetKey].label}
                  </p>
                  {item.error && <p role="alert" style={{ margin: ".15rem 0 0", color: "#FDA4AF", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.4 }}>{item.error}</p>}
                </div>

                {item.status === "uploading" && <Loader2 size={16} className="animate-spin" aria-hidden="true" style={{ color: "#FCD34D", flex: "0 0 auto" }} />}
                {item.status === "done" && <CheckCircle2 size={16} aria-hidden="true" style={{ color: "#86EFAC", flex: "0 0 auto" }} />}

                {/*
                  A PDF gets its size choice here rather than in the crop window,
                  which cannot open it. The choice still has to be made: it is the
                  size the server rasterizes the first page to.
                */}
                {item.status !== "uploading" && item.status !== "done" && UNCROPPABLE_TYPES.has(item.file.type) && (
                  <select
                    aria-label={`Output size for ${item.file.name}`}
                    value={item.presetKey}
                    onChange={event => updateItem(item.id, { presetKey: event.target.value as ImagePresetKey })}
                    style={{ minHeight: 34, padding: "0 .4rem", borderRadius: ".5rem", border: "1px solid rgba(255,255,255,.12)", background: "#111827", color: "#CBD5E1", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", cursor: "pointer", flex: "0 0 auto" }}
                  >
                    {presets.map(preset => <option key={preset.key} value={preset.key}>{preset.label}</option>)}
                  </select>
                )}

                {item.status !== "uploading" && item.status !== "done" && (
                  <button
                    type="button"
                    onClick={() => (UNCROPPABLE_TYPES.has(item.file.type) ? void uploadAsIs(item) : openForCrop(item.id))}
                    style={{ minHeight: 34, display: "inline-flex", alignItems: "center", gap: ".3rem", padding: "0 .55rem", borderRadius: ".5rem", border: "1px solid rgba(0,212,255,.3)", background: "rgba(0,212,255,.08)", color: "#7DEAFF", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, cursor: "pointer", flex: "0 0 auto" }}
                  >
                    {UNCROPPABLE_TYPES.has(item.file.type)
                      ? <><ImageUp size={13} aria-hidden="true" /> {item.status === "failed" ? "Try again" : "Upload"}</>
                      : <><Crop size={13} aria-hidden="true" /> {item.status === "failed" ? "Try again" : "Crop"}</>}
                  </button>
                )}

                {item.status !== "uploading" && (
                  <button
                    type="button"
                    onClick={() => removeItem(item.id)}
                    aria-label={`Remove ${item.file.name}`}
                    style={{ minHeight: 34, minWidth: 34, display: "grid", placeItems: "center", borderRadius: ".5rem", border: "1px solid rgba(255,255,255,.12)", background: "transparent", color: "#94A3B8", cursor: "pointer", flex: "0 0 auto" }}
                  >
                    <Trash2 size={14} aria-hidden="true" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
